import { type FC, useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetWorkflow, useCanEditAutomation, type PlainWorkflow } from "@/queries/automation-queries";
import {
    workflowName,
    workflowsUrl,
    isWorkflowDeleting,
    workflowDetailsEditUrl,
    WorkflowDetailsTab,
    workflowDetailsTabFromLocation,
} from "@/utils/automations";
import { WorkflowExecutionsList } from "@/components/automations/AutomationExecutionsList";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown";
import { AutomationGroupAccess } from "@/components/automations/AutomationGroupAccess";
import { ShareAutomationButton } from "@/components/automations/ShareAutomationButton";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Text } from "@/components/podkit/typography/Text";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";

import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationDefinitionSectionView } from "@/routes/automations/automation-details-page/AutomationDefinitionSection";
import { Button } from "@/components/flexkit/Button";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";
import { Banner } from "@/components/flexkit/Banner";
import {
    convertWorkflowActionToFormSteps,
    convertTriggersToFormData,
    convertWorkflowActionToFormLimits,
} from "@/components/automations/automation-edit-form-data";
import { useDocumentTitle } from "@/hooks/use-document-title";

const IconPencilWithSize = withGitpodSize(IconPencil);

export const AutomationDetailsPage: FC = () => {
    const location = useLocation();
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { data: workflow, error: workflowError } = useGetWorkflow(workflowId);
    const { canEditAutomation } = useCanEditAutomation(workflowId);
    const [selectedTab, setSelectedTab] = useState(
        workflowDetailsTabFromLocation(location) ?? WorkflowDetailsTab.Executions,
    );

    useDocumentTitle(workflow ? workflowName(workflow) : undefined);

    useEffect(() => {
        const fromHash = workflowDetailsTabFromLocation({ hash: location.hash });
        if (fromHash) {
            setSelectedTab(fromHash);
        }
    }, [location.hash]);

    const changeTab = (value: string) => {
        navigate(`#${value}`, { replace: true });
    };

    const isDeleting = workflow ? isWorkflowDeleting(workflow) : false;
    const steps = useMemo(() => convertWorkflowActionToFormSteps(workflow?.spec?.action), [workflow?.spec?.action]);

    return (
        <div data-testid="workflow-page" className="flex min-h-full flex-col gap-4">
            {workflow ? (
                <div className="flex flex-col gap-1">
                    {/* Header row: breadcrumb + action buttons */}
                    <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                        <Breadcrumbs
                            customBreadcrumbs={[
                                { label: "Automations", to: workflowsUrl() },
                                { label: workflowName(workflow) },
                            ]}
                        />
                        <AutomationDetailsPageActionButtons workflow={workflow} canUpdate={canEditAutomation} />
                    </div>
                    {/* Description and trigger info */}
                    <div className="flex flex-col gap-1">
                        <Text className="text-base text-content-strong">
                            {workflow.metadata?.description || "No description provided."}
                        </Text>
                    </div>
                </div>
            ) : (
                <BreadcrumbsSkeleton itemsCount={2} />
            )}

            {isDeleting && (
                <Banner
                    variant="info"
                    className="mt-4"
                    text="This automation is being deleted. All interactions are disabled."
                />
            )}

            <ErrorMessage error={workflowError} />

            <Tabs className="flex grow flex-col" value={selectedTab} onValueChange={changeTab}>
                <TabsList className="mb-6 w-fit flex-row">
                    <TabsTrigger value={WorkflowDetailsTab.Executions}>Executions</TabsTrigger>
                    <TabsTrigger value={WorkflowDetailsTab.Definition}>Definition</TabsTrigger>
                </TabsList>

                <TabsContent value={WorkflowDetailsTab.Definition} className="flex flex-col data-[state=active]:grow">
                    {workflow ? (
                        <AutomationDefinitionSectionView
                            workflowId={workflow.id}
                            trigger={convertTriggersToFormData(workflow.spec?.triggers ?? [])[0]}
                            limits={convertWorkflowActionToFormLimits(workflow.spec?.action)}
                            steps={steps}
                            executor={workflow.metadata?.executor}
                        />
                    ) : null}
                </TabsContent>

                <TabsContent value={WorkflowDetailsTab.Executions} className="flex flex-col data-[state=active]:grow">
                    {workflow ? <WorkflowExecutionsList workflow={workflow} /> : null}
                </TabsContent>
            </Tabs>
        </div>
    );
};

const AutomationDetailsPageActionButtons: FC<{ workflow: PlainWorkflow; canUpdate: boolean }> = ({
    workflow,
    canUpdate,
}) => {
    const navigate = useNavigate();

    const handleEdit = useCallback(() => {
        navigate(workflowDetailsEditUrl({ id: workflow.id }));
    }, [navigate, workflow.id]);

    return (
        <div className="flex items-center gap-2">
            <AutomationGroupAccess workflowId={workflow.id} />
            <ShareAutomationButton workflowId={workflow.id} />
            {canUpdate && (
                <Button variant="outline" LeadingIcon={IconPencilWithSize} onClick={handleEdit} data-tracking-id="edit">
                    Edit
                </Button>
            )}
            <RunAutomationButton workflow={workflow} data-tracking-id="run" />
            <WorkflowActionsDropdown workflow={workflow} showRunAction={false} buttonSize="md" />
        </div>
    );
};
